import { z } from "zod";
import { Id, IsoDate, Ok, OptionalText } from "./common";
import { defineRoutes } from "./define";
import { ClinicalStatus } from "./episodes";
import { PROBLEMS } from "./problem";

/** Mirrors core/modules/visits/domain/visit-result.ts. */
export const VISIT_RESULTS = ["better", "partial", "no_change", "worse", "await_24h"] as const;
export const VisitResult = z.enum(VISIT_RESULTS);
export type VisitResult = z.infer<typeof VisitResult>;

/** Mirrors core/modules/tasks/domain/task-enums.ts (the tasks contract owns the exported enum). */
const TaskPriority = z.enum(["low", "medium", "high"]);

const Pain = z.number().int().min(0).max(10);

const VisitFields = {
  painBefore: Pain.nullable().optional(),
  painAfter: Pain.nullable().optional(),
  result: VisitResult.nullable().optional(),
  done: OptionalText(4000),
  reaction: OptionalText(4000),
  worked: OptionalText(4000),
  notWorked: OptionalText(4000),
  checkNext: OptionalText(4000),
  nextPlan: OptionalText(4000),
  homework: OptionalText(4000),
  write24: z.boolean().optional(),
};

/** The 90-second capture. `occurredAt` backdates (never in the future). */
export const RecordVisitBody = z.object({
  patientId: Id,
  occurredAt: IsoDate.optional(),
  /** Capture started from a booking — mark it done and link the visit. */
  appointmentId: Id.optional(),
  /** Consume one visit from this package balance. */
  redeemPackageId: Id.optional(),
  ...VisitFields,
});
export type RecordVisitBody = z.infer<typeof RecordVisitBody>;

/**
 * A correction re-sends the complete capture (the form edits every field): a field left out
 * is cleared, not kept. Linkage (patient/episode) stays immutable; `occurredAt` may move.
 */
export const UpdateVisitBody = z.object({
  occurredAt: IsoDate.optional(),
  ...VisitFields,
});
export type UpdateVisitBody = z.infer<typeof UpdateVisitBody>;

export const VisitHistoryItem = z.object({
  id: Id,
  occurredAt: z.string(),
  painBefore: Pain.nullable(),
  painAfter: Pain.nullable(),
  result: VisitResult.nullable(),
  done: z.string().nullable(),
  reaction: z.string().nullable(),
  worked: z.string().nullable(),
  notWorked: z.string().nullable(),
  checkNext: z.string().nullable(),
  nextPlan: z.string().nullable(),
  homework: z.string().nullable(),
  write24: z.boolean(),
});
export type VisitHistoryItem = z.infer<typeof VisitHistoryItem>;

export const ActiveEpisodeView = z.object({
  id: Id,
  title: z.string(),
  clinicalStatus: ClinicalStatus,
  stage: z.number().int(),
  hypothesis: z.string().nullable(),
  goal: z.string().nullable(),
  painStart: Pain.nullable(),
  painNow: Pain.nullable(),
  functionStart: z.string().nullable(),
  functionNow: z.string().nullable(),
  redFlags: z.string().nullable(),
  constraints: z.string().nullable(),
});

export const LastVisitView = z.object({
  occurredAt: z.string(),
  painBefore: Pain.nullable(),
  painAfter: Pain.nullable(),
  done: z.string().nullable(),
  worked: z.string().nullable(),
  notWorked: z.string().nullable(),
  checkNext: z.string().nullable(),
  nextPlan: z.string().nullable(),
});

export const OpenTaskView = z.object({
  id: Id,
  title: z.string(),
  dueDate: z.string().nullable(),
  priority: TaskPriority,
});

/** The <30-second pre-visit review: active episode + last visit + open tasks. */
export const TodayBrief = z.object({
  patientName: z.string(),
  episode: ActiveEpisodeView.nullable(),
  lastVisit: LastVisitView.nullable(),
  openTasks: z.array(OpenTaskView),
});
export type TodayBrief = z.infer<typeof TodayBrief>;

export const visits = defineRoutes({
  record: {
    method: "POST",
    path: "/episodes/:episodeId/visits",
    summary: "Record a visit (the 90-second capture)",
    params: z.object({ episodeId: Id }),
    body: RecordVisitBody,
    responses: {
      201: z.object({ visitId: Id }),
      400: PROBLEMS[400],
      401: PROBLEMS[401],
      403: PROBLEMS[403],
      404: PROBLEMS[404],
      422: PROBLEMS[422],
    },
  },
  update: {
    method: "PATCH",
    path: "/visits/:visitId",
    summary:
      "Correct a recorded visit — full capture replace; episode pain re-syncs from the latest visit",
    params: z.object({ visitId: Id }),
    body: UpdateVisitBody,
    responses: {
      200: Ok,
      400: PROBLEMS[400],
      401: PROBLEMS[401],
      403: PROBLEMS[403],
      404: PROBLEMS[404],
      422: PROBLEMS[422],
    },
  },
  history: {
    method: "GET",
    path: "/episodes/:episodeId/visits",
    summary: "An episode's visits, latest first",
    params: z.object({ episodeId: Id }),
    responses: { 200: z.array(VisitHistoryItem), 401: PROBLEMS[401], 403: PROBLEMS[403] },
  },
  todayBrief: {
    method: "GET",
    path: "/patients/:patientId/brief",
    summary: "What to know before today's visit",
    params: z.object({ patientId: Id }),
    responses: { 200: TodayBrief, 401: PROBLEMS[401], 403: PROBLEMS[403], 404: PROBLEMS[404] },
  },
});
